import * as yup from "yup";
import {
  checkZxcvbnStrength,
  checkHIBP,
  hasUpperCase,
  hasLowercase,
  hasDigit,
  hasSymbol,
} from "./passwordValidation";
import { DateTime } from "luxon";
import { MIN_AGE_APPLICATION } from "./constants";
import { containsHtmlLikeTags } from "./string";

// Allows Unicode letters (incl. accents) + spaces + - ! . _ and apostrophes.
// Includes both straight ' and curly ’ apostrophe.
// Requires at least one letter (prevents inputs like "----" or "!!").
const PERSON_NAME_REGEX = /^(?=.*[\p{L}\p{M}])[-–—\p{L}\p{M} '.!_’§]*$/u;

const PERSON_NAME_MESSAGE = "Only letters, spaces, - ! . _ and ' are allowed.";

const HTML_TAGS_MESSAGE = "HTML or script tags are not allowed.";

export const noHtmlTags = <T extends yup.StringSchema<any>>(schema: T) =>
  schema.test(
    "no-html-tags",
    HTML_TAGS_MESSAGE,
    (value) => !value || !containsHtmlLikeTags(value),
  );

export const passwordField = yup
  .string()
  .trim()
  .required("Password is required")
  .min(12, "Password must be at least 12 characters long.")
  .test("zxcvbn-strength", "Password is too weak or predictable", (value) =>
    value ? checkZxcvbnStrength(value) : false,
  )
  .test(
    "not-compromised",
    "This password has been exposed in a data breach. Please choose a different one.",
    async function (value) {
      if (!value) return false;
      try {
        return await checkHIBP(value);
      } catch (e) {
        console.warn("HIBP check failed", e);
        return true; // Fail open
      }
    },
  )
  .test(
    "hasUpperCase",
    "Must contain at least 1 uppercase character (A-Z)",
    hasUpperCase,
  )
  .test(
    "hasLowercase",
    "Must contain at least 1 lowercase character (a-z)",
    hasLowercase,
  )
  .test("hasDigit", "Must contain at least 1 digit (0-9)", hasDigit)
  .test(
    "hasSpecial",
    "Must contain at least 1 special character (!@#%&)",
    hasSymbol,
  );

export const userSignUpValidation = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required("The first name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),

  lastName: yup
    .string()
    .trim()
    .required("The last name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  password: passwordField,
  confirmPassword: yup
    .string()
    .trim()
    .required("Confirm password is required")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .required("Email address is required field."),
});

export const companyDetailsSignUpValidation = yup.object().shape({
  companyName: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The Company name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(100, "Please enter no more than 100 characters."),
  companyWebsite: yup
    .string()
    .trim()
    .required("The Company website field is required.")
    .matches(
      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
      "Invalid url.",
    ),
  companyType: yup.string().required("The business type field is required."),
  supportEmailAddress: yup
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .required("Email address is required field."),
  supportPhoneNumber: yup
    .string()
    .min(10, "Minimum 10 numbers are required.")
    .max(20, "Please enter no more than 20 characters.")
    .matches(
      /^[0-9 +()-]*$/,
      "Please enter a valid number. The number can only contain digits, spaces, + ( ) -.",
    ),
  companyReg: yup
    .string()
    .trim()
    .max(30, "Please enter no more than 30 characters.")
    .matches(
      /^[A-Za-z0-9 /()-]*$/,
      "Only letters, numbers, spaces, - / ( ) are allowed.",
    ),
  companyIndustry: yup
    .string()
    .trim()
    .required("The Industry field is required."),
  hasAgreedTC: yup
    .boolean()
    .required("Please check the box to agree to the terms and conditions."),
});

export const companyAddressValidation = yup.object().shape({
  address_line_1: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The address line 1 field is required.")
    .min(1, "Please enter at least 1 letter.")
    .max(50, "Please enter no more than 50 characters."),
  address_line_2: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .max(50, "Please enter no more than 50 characters."),
  city: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The city field is required.")
    .max(50, "Please enter no more than 50 characters."),
  country: yup.string().trim().required("The country field is required."),
  postcode: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The post/zip code field is required.")
    .min(1, "Please enter at least 1 letter.")
    .max(10, "Please enter no more than 10 characters.")
    .matches(
      /^[A-Za-z0-9 ]*$/,
      "Only letters, numbers and spaces are allowed.",
    ),
});

export const BrandingValidation = yup.object().shape({
  text: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .max(200, "Please enter no more than 200 characters."),
  additional_information: yup.boolean(),
  additional_text: noHtmlTags(yup.string()).when("additional_information", {
    is: true,
    then: (schema) =>
      schema.test(
        "text-or-link",
        "Provide either Information text or at least one valid link.",
        function (value) {
          const { additional_links } = this.parent;
          const hasLink =
            additional_links &&
            additional_links.some((link) => link.link_text && link.link_url);
          return value || hasLink;
        },
      ),
  }),
  // Only validate links while the section is switched on: the form seeds one
  // blank link row, and validating it while hidden made the form invalid
  // with no visible error (Save stuck disabled).
  additional_links: yup.array().when("additional_information", {
    is: true,
    then: (schema) =>
      schema.of(
        yup.object().shape({
          link_text: noHtmlTags(yup.string()).required(
            "Link text is required.",
          ),
          link_url: yup
            .string()
            .max(200, "Please enter no more than 200 characters.")
            .url("Provide a valid URL.")
            .required("URL is required."),
        }),
      ),
    otherwise: (schema) => schema.notRequired(),
  }),

  termsLink: yup.string().when("display_terms", {
    is: true,
    then: (schema) =>
      schema
        .url("Enter a valid URL.")
        .required("Terms and conditions link is required."),
    otherwise: (schema) => schema.notRequired(),
  }),
  privacyLink: yup.string().when("display_terms", {
    is: true,
    then: (schema) =>
      schema
        .url("Enter a valid URL.")
        .required("Privacy policy link is required."),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const billingInfoValidation = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required("The first name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  lastName: yup
    .string()
    .trim()
    .required("The last name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  email: yup
    .string()
    .email("Please enter a valid email address.")
    .required("Email address is required field."),
  telephone: yup
    .string()
    .trim()
    .required("Telephone number is required")
    .min(10, "Minimum 10 numbers are required.")
    .max(20, "Please enter no more than 20 characters.")
    .matches(
      /^[0-9 +()-]*$/,
      "Please enter a valid number. The number can only contain digits, spaces, + ( ) -.",
    ),
  taxRegNumber: yup
    .string()
    .trim()
    .max(20, "Maximum 20 characters are allowed.")
    .nullable()
    .matches(
      /^[a-zA-Z0-9-/()]*$/,
      "Only numbers, letters, - / ( ) are allowed.",
    ),
});

export const loginValidation = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email")
    .required("Email is required"),
  password: yup.string().trim().required("Password is required"),
});

export const addAdditionalDocumentValidations = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Document name is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(50, "Please enter no more than 50 characters."),
  // .matches(
  //   /^[\s\S]*$/,
  //   "Document name contains invalid characters."
  // ),
  description: yup
    .string()
    .trim()
    .required("Document description is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(200, "Please enter no more than 200 characters."),
  // .matches(
  //   /^[a-zA-Z '!._-]*$/,
  //   "Only letters, spaces, - ! . _ and ' are allowed."
  // ),
  document_capture_type: yup
    .array()
    .of(yup.number())
    .min(1, "Please select at least one option for document side.")
    .required("Please select at least one option for document side."),
});

export const addUserValidation = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required("The first name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  lastName: yup
    .string()
    .trim()
    .required("The last name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .required("Email address is required field."),
  jobTitle: yup
    .string()
    .trim()
    .required("The job title field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(50, "Please enter no more than 50 characters."),
  groups: yup.string().trim().required("Permissions field is required."),
});

export const Primary_Purpose_validation = yup.object().shape({
  primary_purpose: yup
    .string()
    .trim()
    .required("The primary purpose filed is required.")
    .max(250, "Please enter no more than 250 characters."),
});

export const DPO_validation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("The name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .required("Email address is required field."),
  phone_number: yup
    .string()
    .trim()
    .required("Contact number is required")
    .min(10, "Minimum 10 numbers are required.")
    .max(20, "Please enter no more than 20 characters.")
    .matches(
      /^[0-9 +()-]*$/,
      "Please enter a valid number. The number can only contain digits, spaces, + ( ) -.",
    ),
});

export const settingsCompanyDetailsValidation = yup.object().shape({
  companyName: yup
    .string()
    .trim()
    .required("The Company name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(100, "Please enter no more than 100 characters."),
  companyDisplayname: yup
    .string()
    .nullable()
    .trim()
    .min(2, "Please enter at least 2 letters.")
    .max(100, "Please enter no more than 100 characters."),
  companyType: yup
    .string()
    .trim()
    .required("The company type field is required."),
  companyWebsite: yup
    .string()
    .trim()
    .required("The Company website field is required.")
    .matches(
      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
      "Invalid URL.",
    ),
  companyIndustry: yup
    .string()
    .trim()
    .required("The Industry field is required."),
  supportEmailAddress: yup
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .nullable(),
  supportPhoneNumber: yup
    .string()
    .min(10, "Minimum 10 numbers are required.")
    .max(20, "Please enter no more than 20 characters.")
    .matches(
      /^[0-9 +()-]*$/,
      "Please enter a valid number. The number can only contain digits, spaces, + ( ) -.",
    ),
  companyReg: yup
    .string()
    .trim()
    .max(30, "Please enter no more than 30 characters.")
    .nullable()
    .matches(
      /^[A-Za-z0-9 /()-]*$/,
      "Only letters, numbers, spaces, - / ( ) are allowed.",
    ),
});

export const settingsApplicationTypesValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("The name field is required.")
    .min(4, "Please enter at least 4 letters.")
    .max(50, "Please enter no more than 50 characters."),
  description: yup
    .string()
    .trim()
    .required("The description field is required.")
    .min(10, "Please enter at least 10 letters.")
    .max(250, "Please enter no more than 250 characters."),
});

/** Application Type create and edit screens — both require Divisions (no Teams). */
export const settingsNewApplicationTypeValidation =
  settingsApplicationTypesValidation.shape({
    divisions: yup
      .array()
      .of(yup.number())
      .min(1, "Select at least one division."),
  });

/** Create screen only — edit has no Type field, so it can't share this. */
export const settingsCreateApplicationTypeValidation =
  settingsNewApplicationTypeValidation.shape({
    _type: yup
      .string()
      .oneOf(["individual", "entity"], "The type field is required.")
      .required("The type field is required."),
  });

export const passwordResetValidation = yup.object().shape({
  currentPassword: yup.string().required("Current password is required"),
  newPassword: passwordField,
  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
});

export const confirmPasswordResetValidation = yup.object().shape({
  newPassword: passwordField,
  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
});

export const createPasswordValidation = yup.object().shape({
  createPassword: passwordField,
  confirmPassword: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("createPassword"), null], "Passwords must match"),
});

export const emailVerificationValidation = yup.object().shape({
  code: yup
    .string()
    .trim()
    .required("Please enter your code.")
    .max(6, "The verification code is only six digits long.")
    .min(6, "The verification code is six digits long."),
});

export const addMandateValidation = yup.object().shape({
  division: yup.string().trim().required("The division field is required."),
  team: yup.string().trim().required("The team field is required."),
  applicationType: yup
    .string()
    .trim()
    .required("The application type field is required."),
  applicationReference: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The application reference field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(30, "Please enter no more than 30 characters."),
  // consultant: yup.string().trim().required("The consultant field is required."),
  consultantName: yup
    .string()
    .trim()
    .when("consultant", {
      is: "create_consultant",
      then: yup
        .string()
        .trim()
        .required("The consultant name field is required.")
        .min(2, "Please enter at least 2 letters.")
        .max(50, "Please enter no more than 50 characters.")
        .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
    }),
  consultantEmail: yup.string().when("consultant", {
    is: "create_consultant",
    then: yup
      .string()
      .trim()
      .email("Please enter a valid email address.")
      .required("Consultant email is required field."),
  }),
});

export const addDivisionValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The name field is required.")
    .max(100, "Please enter no more than 100 characters."),
  display_name: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The display name field is required.")
    .max(150, "Please enter no more than 150 characters."),
  code: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The code field is required.")
    .max(10, "Please enter no more than 10 characters."),
});

export const addTeamValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The name field is required.")
    .max(100, "Please enter no more than 100 characters."),
  display_name: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The display name field is required.")
    .max(150, "Please enter no more than 150 characters."),
  division: yup
    .number()
    .typeError("The division field is required.")
    .required("The division field is required."),
});

/** For drafting a Team alongside a not-yet-created Division, where there's no division id yet. */
export const teamDraftValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The name field is required.")
    .max(100, "Please enter no more than 100 characters."),
  display_name: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The display name field is required.")
    .max(150, "Please enter no more than 150 characters."),
});

export const yourReferenceValidation = noHtmlTags(
  yup.string().trim(),
).max(50, "Please enter no more than 50 characters.");

export const addAccessRoleValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The name field is required.")
    .max(100, "Please enter no more than 100 characters."),
  description: noHtmlTags(yup.string().trim()).max(
    500,
    "Please enter no more than 500 characters."
  ),
});

export const addEntityReferenceValidation = yup.object().shape({
  yourReference: yourReferenceValidation,
});

export const getAddIndividualValidation = (opts?: {
  requireEmail?: boolean;
  requireTitle?: boolean;
  requireDivisionTeam?: boolean;
}) => {
  const requireEmail = opts?.requireEmail ?? false;
  const requireTitle = opts?.requireTitle ?? false;
  const requireDivisionTeam = opts?.requireDivisionTeam ?? true;

  return yup.object().shape({
    division: requireDivisionTeam
      ? yup.string().trim().required("The division field is required.")
      : yup.string().trim().nullable(),
    team: requireDivisionTeam
      ? yup.string().trim().required("The team field is required.")
      : yup.string().trim().nullable(),
    gender: yup
      .string()
      .nullable()
      .required("The gender field is required."),
    title: requireTitle
      ? yup.string().trim().required("The title field is required.").nullable()
      : yup.string().trim().nullable(),
    firstName: yup
      .string()
      .trim()
      .required("The first name field is required.")
      .min(2, "Please enter at least 2 letters.")
      .max(25, "Please enter no more than 25 characters.")
      .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
    lastName: yup
      .string()
      .trim()
      .required("The last name field is required.")
      .min(2, "Please enter at least 2 letters.")
      .max(25, "Please enter no more than 25 characters.")
      .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
    email: yup
      .string()
      .trim()
      .email("Please enter a valid email address.")
      .when([], {
        is: () => requireEmail,
        then: (s) => s.required("Email address is required field."),
        otherwise: (s) => s.notRequired(),
      }),
    middleName: yup
      .string()
      .trim()
      .min(2, "Please enter at least 2 letters.")
      .max(25, "Please enter no more than 25 characters.")
      .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
    yourReference: yourReferenceValidation,
  });
};

export const addIndividualValidationOptionalEmail = getAddIndividualValidation({
  requireEmail: false,
  requireTitle: true,
});

const isValidISODate = (v?: string | null) => !v || DateTime.fromISO(v).isValid;
const isValidDisplayDate = (v?: string | null) =>
  !v || DateTime.fromFormat(v, "dd/MM/yyyy").isValid;
const isValidDateInput = (v?: string | null) =>
  !v || isValidISODate(v) || isValidDisplayDate(v);
const parseDateInput = (v?: string | null) => {
  if (!v) return null;

  const isoDate = DateTime.fromISO(v);
  if (isoDate.isValid) {
    return isoDate;
  }

  const displayDate = DateTime.fromFormat(v, "dd/MM/yyyy");
  return displayDate.isValid ? displayDate : null;
};

const maxDobISO = () =>
  DateTime.now().minus({ years: MIN_AGE_APPLICATION }).toISODate() ?? "";

export const personEditValidation = yup.object({
  firstName: noHtmlTags(yup.string().trim()).required("First name is required"),
  middleName: noHtmlTags(yup.string().trim()).nullable(),
  lastName: noHtmlTags(yup.string().trim()).required("Last name is required"),
  gender: yup.string().trim().required("Gender is required"),
  // country: yup
  //   .number()
  //   .typeError("Country is required")
  //   .nullable()
  //   .required("Country is required"),
  dateOfBirth: yup
    .string()
    .test("valid-dob", "Date of birth must be a valid date", (v) =>
      !v ? true : isValidDateInput(v),
    )
    .test(
      "dob-min-age",
      `Must be at least ${MIN_AGE_APPLICATION} years old`,
      (v) => {
        if (!v) return true; // allow empty
        const dateValue = parseDateInput(v);
        return Boolean(dateValue && dateValue <= DateTime.fromISO(maxDobISO()));
      },
    ),
});

export const entityEditValidation = yup.object({
  name: noHtmlTags(yup.string().trim()).required("Entity name is required"),
  entityType: yup
    .number()
    .typeError("Entity type is required")
    .nullable()
    .required("Entity type is required"),
  registrationNumber: yup.string().trim().concat(noHtmlTags(yup.string())),
  // .required("Registration number is required"),
  country: yup
    .number()
    .typeError("Country is required")
    .nullable()
    .required("Country is required"),
  status: yup.string().trim().nullable(),

  dateOfRegistration: yup
    .string()
    .nullable()
    .test("valid-inc-date", "Date of incorporation must be a valid date", (v) =>
      isValidDateInput(v ?? ""),
    )
    .test(
      "inc-not-future",
      "Date of incorporation cannot be in the future",
      (v) => {
        if (!v) return true;
        const dateValue = parseDateInput(v);
        return Boolean(dateValue && dateValue <= DateTime.now());
      },
    ),

  dissolutionDate: yup
    .string()
    .nullable()
    .test("valid-dis-date", "Dissolution date must be a valid date", (v) =>
      isValidISODate(v ?? ""),
    )
    .test(
      "dis-after-inc",
      "Dissolution date cannot be before incorporation date",
      function (v) {
        if (!v) return true;
        const inc = this.parent.dateOfRegistration;
        if (!inc) return true;
        const dissolutionDate = parseDateInput(v);
        const incorporationDate = parseDateInput(inc);

        if (!dissolutionDate || !incorporationDate) return false;

        return dissolutionDate >= incorporationDate;
      },
    ),
});

export const addressFormValidationSchema = (countryCode?: string) =>
  yup
    .object()
    .shape({
      building_number: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(50, "Please enter no more than 50 characters."),
      flat_appartment_subbuilding: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(75, "Please enter no more than 75 characters."),
      building_name: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(100, "Please enter no more than 100 characters."),
      road_street: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(100, "Please enter no more than 100 characters.")
        .min(1, "Please enter at least 1 character."),
      town_city: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(75, "Please enter no more than 75 characters.")
        .min(2, "Please enter at least 2 character."),
      district: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(75, "Please enter no more than 75 characters.")
        .min(2, "Please enter at least 2 characters."),
      state_province_name: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(75, "Please enter no more than 75 characters.")
        .min(2, "Please enter at least 2 characters.")
        .nullable(),

      state_province_code: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(10, "Please enter no more than 10 characters.")
        .min(2, "Please enter at least 2 characters.")
        .nullable(),
      post_zip_code: yup
        .string()
        .concat(noHtmlTags(yup.string()))
        .max(15, "Please enter no more than 15 characters.")
        .min(2, "Please enter at least 2 character."),
    })
    .test(
      "at-least-one",
      "At least one of building number, building name or flat number is required",
      function (value) {
        const { flat_appartment_subbuilding, building_number, building_name } =
          value;

        if (
          !flat_appartment_subbuilding &&
          !building_number &&
          !building_name
        ) {
          const path = !building_number
            ? "building_number"
            : !flat_appartment_subbuilding
            ? "flat_appartment_subbuilding"
            : "road_street";

          return this.createError({
            path,
            message:
              "At least one of building number, building name or flat number is required",
          });
        }

        // Return true to indicate validation success
        return true;
      },
    )
    .test(
      "us-state-required",
      "State is required for US addresses.",
      function (value) {
        const { state_province_name, state_province_code } = value;
        const isUS = countryCode === "US";

        if (!isUS) return true;

        if (
          (!state_province_name || state_province_name.trim() === "") &&
          (!state_province_code || state_province_code.trim() === "")
        ) {
          return this.createError({
            path: "state_province_name",
            message: "State is required for US addresses.",
          });
        }

        return true;
      },
    );

export const companyCheckValidationSchema = yup.object().shape({
  division: yup.string().trim().required("The division field is required."),
  team: yup.string().trim().required("The team field is required."),
  companyName: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .required("The Company name field is required.")
    .min(2, "Please enter at least 2 characters.")
    .max(100, "Please enter no more than 100 characters.")
    .matches(
      /^[A-Za-z0-9&@.,'’‘\-() ]+$/,
      "Company name contains invalid characters.",
    ),
  country: yup.string().trim().required("The country field is required."),
  registrationNumber: yup
    .string()
    .trim()
    .concat(noHtmlTags(yup.string()))
    .max(15, "Please enter no more than 15 characters.")
    .matches(
      /^[A-Za-z0-9 /()-]*$/,
      "Only letters, numbers, spaces, - / ( ) are allowed.",
    ),
});

export const addChecksIndividualValidation = yup.object().shape({
  division: yup.string().trim().required("The division field is required."),
  team: yup.string().trim().required("The team field is required."),
  firstName: yup
    .string()
    .trim()
    .required("The first name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  lastName: yup
    .string()
    .trim()
    .required("The last name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  middleName: yup
    .string()
    .trim()
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  gender: yup.string().required("Gender is required"),
});

export const atLeastOneMonitoringSelected = (
  message = "At least one of Sanctions or Adverse media monitoring must be Yes.",
) =>
  yup.object().test("at-least-one", message, function (value) {
    const v: any = value || {};
    const pepRaw = v.pep_and_sanction_required;
    const advRaw = v.adverse_media_required;

    const pep = pepRaw === true || pepRaw === "true";
    const adverse = advRaw === true || advRaw === "true";

    if (!pep && !adverse) {
      return this.createError({
        path: "pep_and_sanction_required",
        message,
      });
    }

    return true;
  });

export const newCompanyMonitoringFormValidation = yup
  .object()
  .shape({
    division: yup.string().trim().required("The division field is required."),
    team: yup.string().trim().required("The team field is required."),
    company_name: yup
      .string()
      .trim()
      .required("The Company name field is required.")
      .min(2, "Please enter at least 2 characters.")
      .max(100, "Please enter no more than 100 characters.")
      .matches(
        /^[A-Za-z0-9&@.,'’‘\-() ]+$/,
        "Company name contains invalid characters.",
      ),
    country: yup
      .string()
      .trim()
      .nullable()
      .required("The country field is required."),
    registration_number: yup
      .string()
      .trim()
      .max(15, "Please enter no more than 15 characters.")
      .matches(
        /^[A-Za-z0-9 /()-]*$/,
        "Only letters, numbers, spaces, - / ( ) are allowed.",
      ),
    pep_and_sanction_required: yup
      .string()
      .nullable()
      .required("This field is required"),
    adverse_media_required: yup
      .string()
      .nullable()
      .required("This field is required"),
  })
  .concat(
    atLeastOneMonitoringSelected(
      "At least one of PEP & sanctions monitoring or Adverse media monitoring must be Yes.",
    ),
  );

export const newMonitoringFormValidation = yup
  .object()
  .shape({
    division: yup.string().trim().required("The division field is required."),
    team: yup.string().trim().required("The team field is required."),
    title: yup.string().trim().required("The title field is required."),
    firstName: yup
      .string()
      .trim()
      .required("The first name field is required.")
      .min(2, "Please enter at least 2 letters.")
      .max(25, "Please enter no more than 25 characters.")
      .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
    lastName: yup
      .string()
      .trim()
      .required("The last name field is required.")
      .min(2, "Please enter at least 2 letters.")
      .max(25, "Please enter no more than 25 characters.")
      .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
    middleName: yup
      .string()
      .trim()
      .min(2, "Please enter at least 2 letters.")
      .max(25, "Please enter no more than 25 characters.")
      .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
    gender: yup.string().required("This field is required"),
    pep_and_sanction_required: yup.string().required("This field is required"),
    adverse_media_required: yup.string().required("This field is required"),
  })
  .concat(atLeastOneMonitoringSelected());

export const monitoringEventsValidation = yup
  .object()
  .shape({
    pep_and_sanction_required: yup.boolean().required("This field is required"),
    adverse_media_required: yup.boolean().required("This field is required"),
  })
  .concat(atLeastOneMonitoringSelected());

export const addEmailValidation = yup.object().shape({
  emailAddress: yup
    .string()
    .trim()
    .email("Please enter a valid email")
    .required("Required field"),
});

export const validateForm = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Form name is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(150, "Please enter no more than 150 characters."),
  // .matches(
  //   /^[a-zA-Z0-9 '!._-]*$/,
  //   "Only letters, numbers, spaces, - ! . _ and ' are allowed."
  // ),
  description: yup
    .string()
    .trim()
    .required("Form description is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(500, "Please enter no more than 500 characters."),
  // .matches(
  //   /^[a-zA-Z '!._-]*$/,
  //   "Only letters, spaces, - ! . _ and ' are allowed."
  // ),
});

export const getValidationSchema = yup.object().shape({
  sections: yup.array().of(
    yup.object().shape({
      title: yup
        .string()
        .trim()
        .required("Section title is required.")
        .min(2, "Please enter at least 2 letters.")
        .max(250, "Please enter no more than 250 characters."),
      description: yup
        .string()
        .trim()
        .required("Section description is required.")
        .min(2, "Please enter more than 2 characters.")
        .max(500, "Please enter no more than 500 characters."),
      questions: yup
        .array()
        .min(1, "Each section must have at least one question."),
    }),
  ),
});

export const validateQuestionForm = yup.object().shape({
  title: yup
    .string()
    .trim()
    .required("Title is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(250, "Please enter no more than 250 characters."),

  description: yup
    .string()
    .trim()
    .min(2, "Please enter at least 2 letters.")
    .max(500, "Please enter no more than 500 characters."),

  field_type: yup.mixed().required("Field type is required."),

  options: yup.array().when("field_type", {
    is: (fieldType) =>
      typeof fieldType === "object"
        ? fieldType.slug_name === "file_upload"
        : fieldType === "file_upload",
    then: yup
      .array()
      .of(
        yup.object().shape({
          label: yup.string().required("File type label is required"),
          value: yup.string().required("File type value is required"),
        }),
      )
      .min(1, "Please select at least one file type")
      .required("File type is required"),

    otherwise: yup.array().when("field_type", {
      is: (fieldType) => {
        const slug =
          typeof fieldType === "object" ? fieldType.slug_name : fieldType;
        return slug === "choice" || slug === "multi_select";
      },
      then: yup
        .array()
        .of(
          yup.object().shape({
            label: yup
              .string()
              .required("Option label is required")
              .test(
                "no-commas",
                "Commas are not allowed",
                (value) => !value?.includes(","),
              ),
            value: yup
              .string()
              .required("Option value is required")
              .test(
                "no-commas",
                "Commas are not allowed",
                (value) => !value?.includes(","),
              ),
          }),
        )
        .min(2, "Please enter at least two options")
        .required("Options are required"),
      otherwise: yup.array().notRequired(),
    }),
  }),
});

export const codeValidationSchema = yup.object().shape({
  verificationCode: yup
    .string()
    .length(6, "The code must be exactly 6 digits")
    .matches(/^\d+$/, "The code must contain only numbers")
    .required("This field is required"),
});

export const addEditConsultantValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("The first name field is required.")
    .min(2, "Please enter at least 2 letters.")
    .max(25, "Please enter no more than 25 characters.")
    .matches(PERSON_NAME_REGEX, PERSON_NAME_MESSAGE),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .required("Email address is required field."),
});

export const addressTypeValidationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Address Type Name is required.")
    .max(100, "Ensure this field has no more than 100 characters."),
  type: yup
    .string()
    .nullable()
    .oneOf(["individual", "entity"])
    .required("Type is required."),
});

export const entityTypeValidationSchema = yup.object().shape({
  name: noHtmlTags(yup.string().trim())
    .required("Entity type name is required.")
    .max(50, "Ensure this field has no more than 50 characters."),
  risk_level: yup
    .string()
    .nullable()
    .oneOf(["low", "medium", "high"], "Risk level is required.")
    .required("Risk level is required."),
});

export const industryValidationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Industry Name is required.")
    .max(100, "Ensure this field has no more than 100 characters."),
  code: yup
    .string()
    .trim()
    .nullable()
    .max(100, "Ensure this field has no more than 100 characters."),
  type: yup
    .string()
    .oneOf(["individual", "entity"])
    .required("Type is required."),
  risk: yup
    .string()
    .oneOf(["low", "medium", "high"])
    .required("Risk level is required."),
  active: yup.boolean(),
});

export const natureOfBusinessValidationSchema = yup.object().shape({
  description: noHtmlTags(yup.string().trim()).required(
    "Description is required.",
  ),
  industryIds: yup
    .array()
    .of(yup.number())
    .min(1, "Select at least one industry."),
  sectorIds: yup.array().of(yup.number()).min(1, "Select at least one sector."),
});

export const formerNameValidationSchema = (isEdit: boolean) =>
  yup.object().shape({
    name: noHtmlTags(yup.string().trim()).required(
      "Previous name is required.",
    ),
    ceasedOn: yup.string().nullable().required("Ceased on date is required."),
    changeDate: isEdit
      ? yup.string().nullable().required("Change date is required.")
      : yup.string().nullable(),
    reason: noHtmlTags(yup.string().trim()).required("Reason is required."),
  });

export const profileRelationshipTypeValidationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Name is required.")
    .max(150, "Ensure this field has no more than 150 characters."),
  entity_type: yup
    .number()
    .typeError("Entity type is required.")
    .required("Entity type is required."),
  risk_level: yup
    .string()
    .nullable()
    .oneOf(["low", "medium", "high"])
    .required("Risk level is required."),
  percentage: yup.boolean().optional(),
  description: yup.boolean().optional(),
});
